import axiosServices from 'utils/axios';
import type { AxiosResponse } from 'axios';

/**
 * Storage ("Guardado") feature — plain axios calls, Zod-parsed at the query boundary in
 * ./queries.ts, never here (same split as features/shipments/api.ts + contracts/shipment.ts).
 *
 * Every endpoint below answers inside a `{ success: true, data: ... }` envelope — confirmed
 * against nexvia-api-web's real handlers this session (NEX-1151, same branch), unlike this
 * app's legacy `/shipments` and `/quotes` endpoints, which respond with the bare object.
 * Callers read `res.data.data` (or `res.data.data.requests`), never `res.data` directly — see
 * contracts/storage.ts's header for the endpoint-by-endpoint detail.
 */

/** `GET /storage/requests/mine` — every storage request this client has published, any status. */
export const getMyStorageRequests = (): Promise<AxiosResponse<unknown>> => axiosServices.get('/storage/requests/mine');

/** `GET /storage/requests/:id/quotes` — a client's own request plus every quote sent to it. */
export const getStorageRequestQuotes = (requestId: number | string): Promise<AxiosResponse<unknown>> =>
  axiosServices.get(`/storage/requests/${requestId}/quotes`);

/**
 * Starts a Mercado Pago checkout (pago único) for a storage quote. `options` mirrors
 * features/quotes/api.ts's `createCheckout` — a `{ force: true }` retry, or an
 * `{ expectedClientAmount }` first attempt — on the chance this payment module grows the same
 * price-change-detection the shipment one has. Today's real backend body is only
 * `{ storageQuoteId }` (storage quotes have no edit path, so "the price changed mid-checkout"
 * isn't reachable yet — `create-storage-checkout.handler.ts`'s own docblock), so an empty
 * options bag is a no-op for now.
 */
export const createStorageCheckout = (
  storageQuoteId: number | string,
  options: Record<string, unknown> = {}
): Promise<AxiosResponse<unknown>> => axiosServices.post('/storage/payment/checkout', { storageQuoteId, ...options });

/**
 * Starts a Mercado Pago subscription (cobro recurrente mensual) for a storage quote. NEX-1169 (plan 2026-10-20
 * task 1.5) — `autoRenew`: «Renovar automáticamente cada mes hasta que lo canceles» (false = the charge ends
 * with the request's period, the API's default).
 */
export const createStorageSubscription = (
  storageQuoteId: number | string,
  { autoRenew = false }: { autoRenew?: boolean } = {}
): Promise<AxiosResponse<unknown>> => axiosServices.post('/storage/payment/subscribe', { storageQuoteId, autoRenew });

/** `GET /storage/payment/status/:storageRequestId` — polling target for the payment flow. */
export const getStoragePaymentStatus = (storageRequestId: number | string): Promise<AxiosResponse<unknown>> =>
  axiosServices.get(`/storage/payment/status/${storageRequestId}`);

// ── Shipment quote checkout — called from this feature's own parallel-payment screen (NEX-1151) ──
//
// `createShipmentQuoteCheckout` is a deliberate, hand-kept-in-sync DUPLICATE of
// features/quotes/api.ts's `createCheckout` (`POST /payment/checkout`, same `{ quoteId,
// ...options }` body), not an import of it — AGENTS.md §2's coupling rule forbids a feature
// importing another feature's internals ("a file under src/features/X/ may import only
// @nexvia-logistics/ui, @nexvia-logistics/tokens, domain/*, contracts/*, shared/* — NEVER
// another src/features/Y/ directly"), and this endpoint has no shared/ home today (unlike
// `getQuotesByShipment`, which already lives in shared/api/quote.ts and is imported from there
// in ./queries.ts instead of being duplicated — that one genuinely didn't need a copy).
// StorageParallelPaymentPage.tsx needs this call to pay the shipment-side quote in the same
// action as the storage-side one; mirrors features/shipments/api.ts's own
// `createStorageRequestFromWizard`, the identical resolution for the opposite direction.
export const createShipmentQuoteCheckout = (
  quoteId: number | string,
  options: Record<string, unknown> = {}
): Promise<AxiosResponse<unknown>> => axiosServices.post('/payment/checkout', { quoteId, ...options });

// ── NEX-1170 — the client's guardado lifecycle (client-guardado.handler.ts), `{ success, data }` too ──

/** `POST /storage/requests/:id/cancel` — "Dar de baja": the API calculates the end date. */
export const cancelStorageGuardado = (requestId: number | string): Promise<AxiosResponse<unknown>> =>
  axiosServices.post(`/storage/requests/${requestId}/cancel`, {});

/**
 * NEX-1169 (plan 2026-10-20 task 1.8) — `POST /storage/requests/:id/renew { months }` (1–36): a new pago único
 * checkout for that many more months of a one-time guardado (the 501 it answered while payments were on hold
 * is gone).
 */
export const renewStorageGuardado = (requestId: number | string, months: number): Promise<AxiosResponse<unknown>> =>
  axiosServices.post(`/storage/requests/${requestId}/renew`, { months });

/**
 * NEX-1169 (plan 2026-10-20 task 1.5) — `POST /storage/requests/:id/subscription/cancel`: «Cancelar el cobro
 * mensual» in Mercado Pago (the body's optional `reason` is not asked for).
 */
export const cancelStorageSubscription = (requestId: number | string): Promise<AxiosResponse<unknown>> =>
  axiosServices.post(`/storage/requests/${requestId}/subscription/cancel`, {});

/** The destination of a withdrawal, named like the shipment wizard's form fields. */
export interface StorageWithdrawalDestination {
  destinationAddress: string;
  destinationDepartment?: string;
  destinationCity?: string;
  destinationNeighborhood?: string;
  destinationLat?: number;
  destinationLng?: number;
  destinationPlaceId?: string;
  destinationPlaceName?: string;
}

/** `POST /storage/requests/:id/withdrawal` — a shipment draft from the depósito to `destination`. */
export const requestStorageWithdrawal = (
  requestId: number | string,
  destination: StorageWithdrawalDestination
): Promise<AxiosResponse<unknown>> => axiosServices.post(`/storage/requests/${requestId}/withdrawal`, destination);

/** `POST /storage/requests/:id/self-withdrawal` — "Retiré por mi cuenta": closes the guardado. */
export const selfWithdrawStorageGuardado = (requestId: number | string): Promise<AxiosResponse<unknown>> =>
  axiosServices.post(`/storage/requests/${requestId}/self-withdrawal`, {});

// ── NEX-1329 — the guardado's mutual reviews (guardado-review.handler.ts), `{ success, data }` too ──

/** What the client sends to rate the depósito: 1-5 stars, a comment of up to 1000 characters. */
export interface StorageReviewPayload {
  rating: number;
  comment?: string;
}

/** `POST /storage/requests/:id/reviews` — the client rates the depósito, once (decision A6). */
export const submitStorageReview = (requestId: number | string, payload: StorageReviewPayload): Promise<AxiosResponse<unknown>> =>
  axiosServices.post(`/storage/requests/${requestId}/reviews`, payload);

// NEX-1329 (audit W5) — the latest 20 (the API clamps `limit` to 1-50, default 10), like the carrier's dialog.
const DEPOT_REVIEWS_PAGE_SIZE = 20;

/** `GET /storage/depots/:storageProfileId/reviews` — what the depósito's clients said (contract C2). */
export const getStorageDepotReviews = (storageProfileId: number | string): Promise<AxiosResponse<unknown>> =>
  axiosServices.get(`/storage/depots/${storageProfileId}/reviews`, { params: { limit: DEPOT_REVIEWS_PAGE_SIZE, offset: 0 } });
